import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import useSeo from '@/hooks/useSeo';
import { articles } from '@/mocks/siteData';
import { formatViews } from '@/utils/format';

export default function ArticlePage() {
  const { id } = useParams();
  const article = articles.find((item) => item.id === id);
  const [toast, setToast] = useState('');
  const toastTimer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (toastTimer.current) window.clearTimeout(toastTimer.current);
    };
  }, []);

  const showToast = (message: string) => {
    setToast(message);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(''), 2200);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast('链接已复制，快分享给一起吃瓜的朋友');
    } catch {
      showToast('复制失败，请手动复制浏览器地址');
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share && article) {
      try {
        await navigator.share({ title: article.title, url: window.location.href });
      } catch {
        /* 用户取消分享，无需处理 */
      }
    } else {
      handleCopy();
    }
  };

  const related = article
    ? articles.filter((item) => item.id !== article.id && item.category === article.category).slice(0, 3)
    : [];
  const fallbackRelated = article
    ? articles.filter((item) => item.id !== article.id).sort((a, b) => b.hot - a.hot).slice(0, 3)
    : [];
  const relatedList = related.length > 0 ? related : fallbackRelated;

  const crumbs = [
    { label: '首页', to: '/' },
    { label: '最新爆料', to: '/category/all' },
    ...(article ? [{ label: article.categoryName, to: `/category/${article.category}` }, { label: article.title }] : []),
  ];

  useSeo({
    title: article ? `${article.title} - 爆料吃瓜现场` : '内容未找到 - 爆料吃瓜',
    description: article ? article.excerpt : '该条爆料整理暂未找到，可返回首页查看最新进展。',
    keywords: article ? `爆料吃瓜,${article.tags.join(',')},爆料吃瓜最新进展` : '爆料吃瓜',
    path: `/article/${id ?? ''}`,
    jsonLd: article
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'NewsArticle',
            headline: article.title,
            description: article.excerpt,
            datePublished: article.date,
            dateModified: article.date,
            articleSection: article.categoryName,
            keywords: article.tags.join(','),
            inLanguage: 'zh-CN',
            author: { '@type': 'Organization', name: '爆料吃瓜' },
            publisher: { '@type': 'Organization', name: '爆料吃瓜' },
            mainEntityOfPage: `https://rudolf1922.cn/article/${article.id}`,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: crumbs.map((crumb, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              name: crumb.label,
              item: crumb.to ? `https://rudolf1922.cn${crumb.to}` : undefined,
            })),
          },
        ]
      : undefined,
  });

  if (!article) {
    return (
      <SiteLayout>
        <div className="flex flex-col items-center justify-center px-4 py-24 text-center">
          <i className="ri-error-warning-line text-5xl text-foreground-400"></i>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">这条爆料不存在或已下线</h1>
          <p className="mt-2 font-body text-sm text-foreground-600">可能链接有误，去最新爆料看看其他进展吧。</p>
          <Link
            to="/category/all"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回最新爆料
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="w-full px-4 py-6 md:px-6 md:py-8">
          <Breadcrumb items={crumbs} />
          <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <span className="inline-flex rounded-sm bg-primary-100 px-2.5 py-1 font-label text-xs font-medium text-primary-700">
                {article.categoryName}
              </span>
              <h1 className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
                {article.title}
              </h1>
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 font-label text-sm text-foreground-600">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line text-base"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line text-base"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line text-base"></i>
                  {formatViews(article.views)} 次围观
                </span>
                <span className="flex items-center gap-1.5 text-primary-600">
                  <i className="ri-refresh-line text-base"></i>
                  持续更新中
                </span>
              </div>
            </div>
            <div className="relative h-48 w-full overflow-hidden rounded-lg border border-background-200 md:h-56 lg:h-auto">
              <img
                src={article.cover}
                alt={article.title}
                title={`${article.title} - 爆料吃瓜现场`}
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 py-8 md:px-6 md:py-10">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <article className="lg:col-span-2">
            <p className="border-l-4 border-primary-500 bg-background-100 px-5 py-4 font-body text-sm leading-relaxed text-foreground-800 md:text-base">
              {article.excerpt}
            </p>

            <section className="mt-10" aria-labelledby="timeline-title">
              <h2 id="timeline-title" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                进展时间线
              </h2>
              <ol className="mt-5 space-y-0 border-l border-background-300 pl-6">
                {article.timeline.map((node, index) => (
                  <li key={node.time} className="relative pb-6 last:pb-0">
                    <span
                      className={`absolute -left-[31px] top-1 h-3 w-3 rounded-full ring-4 ring-background-50 ${
                        index === 0 ? 'bg-primary-500' : 'bg-secondary-400'
                      }`}
                    ></span>
                    <span className="font-label text-xs font-medium text-primary-600">{node.time}</span>
                    <p className="mt-1 font-body text-sm leading-relaxed text-foreground-800">{node.text}</p>
                  </li>
                ))}
              </ol>
            </section>

            <section className="mt-10" aria-labelledby="content-title">
              <h2 id="content-title" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                事件梳理
              </h2>
              <div className="mt-5 space-y-5">
                {article.content.map((block, index) => {
                  if (block.type === 'h2') {
                    return (
                      <h3 key={index} className="pt-2 font-heading text-lg font-semibold text-foreground-950">
                        {block.text}
                      </h3>
                    );
                  }
                  if (block.type === 'quote') {
                    return (
                      <blockquote
                        key={index}
                        className="rounded-lg bg-accent-50 px-5 py-4 font-heading text-base font-medium leading-relaxed text-accent-900"
                      >
                        {block.text}
                      </blockquote>
                    );
                  }
                  return (
                    <p key={index} className="font-body text-sm leading-7 text-foreground-700 md:text-base">
                      {block.text}
                    </p>
                  );
                })}
              </div>
            </section>

            <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
              <span className="font-label text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/category/${article.category}`}
                  className="rounded-full bg-secondary-100 px-3 py-1 font-label text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
                >
                  {tag}
                </Link>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-4">
              <span className="font-label text-sm text-foreground-600">分享这条瓜：</span>
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 font-label text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <i className="ri-link"></i>
                复制链接
              </button>
              <button
                type="button"
                onClick={handleNativeShare}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 font-label text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <i className="ri-share-forward-line"></i>
                分享给朋友
              </button>
            </div>
          </article>

          <aside className="lg:col-span-1">
            <div className="sticky top-20">
              <h2 className="flex items-center gap-2 border-b border-background-200 pb-3 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-bookmark-3-line text-primary-500"></i>
                相关整理
              </h2>
              <div className="mt-4 flex flex-col gap-4">
                {relatedList.map((item) => (
                  <ArticleCard key={item.id} article={item} variant="list" />
                ))}
              </div>
              <Link
                to={`/category/${article.category}`}
                className="mt-5 inline-flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-secondary-500 px-4 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-secondary-600"
              >
                查看「{article.categoryName}」全部整理
                <i className="ri-arrow-right-line text-base"></i>
              </Link>
            </div>
          </aside>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-24 left-1/2 z-50 -translate-x-1/2 rounded-md bg-foreground-950 px-5 py-3 font-label text-sm text-background-50">
          {toast}
        </div>
      )}
    </SiteLayout>
  );
}